  if (!customElements.get('ctm-winapply')) {
    customElements.define('ctm-winapply', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          this.bindGlobalEvents();
          return;
        }
        this.initialized = true;
        this.form = this.querySelector('[data-form]');
        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        this.progress = this.querySelector('[data-progress]');
        this.progressLabel = this.querySelector('[data-progress-label]');
        this.progressPercent = this.querySelector('[data-progress-percent]');
        this.progressFill = this.querySelector('[data-progress-fill]');
        this.backButton = this.querySelector('[data-back]');
        this.nextButton = this.querySelector('[data-next]');
        this.submitButton = this.querySelector('[data-submit]');
        this.submitButtonText = 'Send application';
        this.apiBaseUrl = String(window.theme?.tps?.baseUrl || '').replace(/\/+$/, '');
        this.submitEndpoint = `${this.apiBaseUrl}/win/applications/submit`;
        this.shopId = this.dataset.shopId || null;
        this.submitting = false;
        this.continueButtonText = this.nextButton?.textContent.trim() || 'Continue';
        this.reviewConfirmText = this.dataset.reviewConfirmText?.trim() || 'Confirm';
        this.nav = this.querySelector('[data-nav]');
        this.success = this.querySelector('[data-success]');
        this.startAgainButton = this.querySelector('[data-start-again]');
        this.card = this.querySelector('[data-card]');
        this.intro = this.querySelector('.ctm-wap_intro');
        this.submitError = this.querySelector('[data-submit-error]');
        this.current = 0;
        this.dirty = false;
        this.completed = false;
        this.editingFromReview = false;
        this.reviewEditStepType = '';
        this.photoItems = { address: [], storefront: [], interior: [] };
        this.photoId = 0;
        this.maxPhotoSize = 10 * 1024 * 1024;
        this.acceptedPhotoTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
        this.acceptedPhotoExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
        this.uploadEndpoint = `${this.apiBaseUrl}/file/presign-upload`;
        this.uploadBusinessType = 'win';
        this.designMode = this.dataset.designMode === 'true';
        this.beforeUnloadHandler = this.handleBeforeUnload.bind(this);
        this.selectOutsideHandler = this.handleSelectOutside.bind(this);
        this.selectTypeahead = '';
        this.selectTypeaheadTimer = 0;
        this.initializeSelects();
        this.bindEvents();
        this.initializeConditions();
        this.initializePhotos();
        this.renderStep(false);
      }

      disconnectedCallback() {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        document.removeEventListener('pointerdown', this.selectOutsideHandler);
        window.clearTimeout(this.selectTypeaheadTimer);
        this.selectTypeahead = '';
      }

      bindGlobalEvents() {
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
        document.addEventListener('pointerdown', this.selectOutsideHandler);
      }

      initializeSelects() {
        this.querySelectorAll('select').forEach((select, selectIndex) => {
          if (select.closest('.ctm-wap_select')) return;
          const wrapper = document.createElement('div');
          const trigger = document.createElement('button');
          const value = document.createElement('span');
          const arrow = document.createElement('span');
          const list = document.createElement('ul');
          const baseId = select.id || `wap-select-${this.dataset.sectionId}-${selectIndex}`;
          const triggerId = `${baseId}-trigger`;
          const valueId = `${baseId}-value`;
          const listId = `${baseId}-listbox`;
          const label = this.querySelector(`label[for="${baseId}"]`);

          wrapper.className = 'ctm-wap_select';
          trigger.className = 'ctm-wap_select-trigger';
          trigger.type = 'button';
          trigger.id = triggerId;
          trigger.setAttribute('aria-haspopup', 'listbox');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.setAttribute('aria-controls', listId);
          value.className = 'ctm-wap_select-value';
          value.id = valueId;
          arrow.className = 'ctm-wap_select-arrow';
          arrow.setAttribute('aria-hidden', 'true');
          list.className = 'ctm-wap_select-list';
          list.id = listId;
          list.setAttribute('role', 'listbox');
          list.tabIndex = -1;
          list.hidden = true;

          if (label) {
            if (!label.id) label.id = `${baseId}-label`;
            label.htmlFor = triggerId;
            trigger.setAttribute('aria-labelledby', `${label.id} ${valueId}`);
            list.setAttribute('aria-labelledby', label.id);
          } else {
            trigger.setAttribute('aria-labelledby', valueId);
            list.setAttribute('aria-labelledby', valueId);
          }

          Array.from(select.options).forEach((option, optionIndex) => {
            const item = document.createElement('li');
            item.className = 'ctm-wap_select-option';
            item.id = `${baseId}-option-${optionIndex}`;
            item.dataset.optionIndex = String(optionIndex);
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
            if (option.disabled) item.setAttribute('aria-disabled', 'true');
            item.textContent = option.textContent;
            list.append(item);
          });

          select.before(wrapper);
          wrapper.append(select, trigger, list);
          trigger.append(value, arrow);
          select.hidden = true;
          select.tabIndex = -1;
          select.setAttribute('aria-hidden', 'true');
          select.dataset.customSelect = '';
          this.syncSelect(select);

          trigger.addEventListener('click', () => {
            if (wrapper.classList.contains('is-open')) this.closeSelect(select, true);
            else this.openSelect(select, 0);
          });
          trigger.addEventListener('keydown', (event) => this.handleSelectTriggerKeydown(event, select));
          list.addEventListener('keydown', (event) => this.handleSelectListKeydown(event, select));
          list.addEventListener('click', (event) => {
            const item = event.target.closest('[data-option-index]');
            if (!item || item.getAttribute('aria-disabled') === 'true') return;
            this.commitSelect(select, Number(item.dataset.optionIndex));
          });
          select.addEventListener('change', () => this.syncSelect(select));
        });
        this.bindGlobalEvents();
      }

      syncSelect(select) {
        const wrapper = select.closest('.ctm-wap_select');
        const trigger = wrapper?.querySelector('.ctm-wap_select-trigger');
        const value = wrapper?.querySelector('.ctm-wap_select-value');
        const options = Array.from(wrapper?.querySelectorAll('[role="option"]') || []);
        const selected = select.options[select.selectedIndex] || select.options[0];
        if (!trigger || !value || !selected) return;
        value.textContent = selected.textContent;
        trigger.classList.toggle('is-placeholder', !selected.value);
        options.forEach((option, index) => option.setAttribute('aria-selected', index === select.selectedIndex ? 'true' : 'false'));
        this.syncSelectAccessibility(select);
      }

      syncSelectAccessibility(select) {
        const trigger = select.closest('.ctm-wap_select')?.querySelector('.ctm-wap_select-trigger');
        if (!trigger) return;
        if (select.getAttribute('aria-invalid') === 'true') trigger.setAttribute('aria-invalid', 'true');
        else trigger.removeAttribute('aria-invalid');
        const describedBy = select.getAttribute('aria-describedby');
        if (describedBy) trigger.setAttribute('aria-describedby', describedBy);
        else trigger.removeAttribute('aria-describedby');
      }

      openSelect(select, direction) {
        this.querySelectorAll('.ctm-wap_select.is-open select').forEach((openSelect) => {
          if (openSelect !== select) this.closeSelect(openSelect, false);
        });
        const wrapper = select.closest('.ctm-wap_select');
        const trigger = wrapper?.querySelector('.ctm-wap_select-trigger');
        const list = wrapper?.querySelector('[role="listbox"]');
        if (!wrapper || !trigger || !list) return;
        wrapper.classList.add('is-open');
        list.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        let index = select.selectedIndex >= 0 ? select.selectedIndex : 0;
        if (direction) index = this.nextSelectIndex(select, index, direction);
        this.setActiveSelectOption(select, index);
        list.focus({ preventScroll: true });
      }

      closeSelect(select, returnFocus) {
        const wrapper = select.closest('.ctm-wap_select');
        const trigger = wrapper?.querySelector('.ctm-wap_select-trigger');
        const list = wrapper?.querySelector('[role="listbox"]');
        if (!wrapper || !trigger || !list) return;
        wrapper.classList.remove('is-open');
        list.hidden = true;
        list.removeAttribute('aria-activedescendant');
        trigger.setAttribute('aria-expanded', 'false');
        wrapper.querySelectorAll('.is-active').forEach((option) => option.classList.remove('is-active'));
        if (returnFocus) trigger.focus();
      }

      setActiveSelectOption(select, index) {
        const list = select.closest('.ctm-wap_select')?.querySelector('[role="listbox"]');
        const options = Array.from(list?.querySelectorAll('[role="option"]') || []);
        const option = options[index];
        if (!list || !option || option.getAttribute('aria-disabled') === 'true') return;
        options.forEach((item) => item.classList.toggle('is-active', item === option));
        list.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }

      activeSelectIndex(select) {
        const active = select.closest('.ctm-wap_select')?.querySelector('.ctm-wap_select-option.is-active');
        return active ? Number(active.dataset.optionIndex) : select.selectedIndex;
      }

      nextSelectIndex(select, start, direction) {
        const options = Array.from(select.options);
        let index = start;
        for (let count = 0; count < options.length; count += 1) {
          index = (index + direction + options.length) % options.length;
          if (!options[index].disabled) return index;
        }
        return start;
      }

      boundarySelectIndex(select, direction) {
        const options = Array.from(select.options);
        const indexes = options.map((option, index) => option.disabled ? -1 : index).filter((index) => index >= 0);
        return direction > 0 ? indexes[indexes.length - 1] : indexes[0];
      }

      commitSelect(select, index) {
        if (!select.options[index] || select.options[index].disabled) return;
        const changed = select.selectedIndex !== index;
        select.selectedIndex = index;
        this.syncSelect(select);
        if (changed) {
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.closeSelect(select, true);
      }

      handleSelectTriggerKeydown(event, select) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.openSelect(select, event.key === 'ArrowDown' ? 1 : -1);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.openSelect(select, 0);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.openSelect(select, 0);
          this.setActiveSelectOption(select, this.boundarySelectIndex(select, event.key === 'End' ? 1 : -1));
        }
      }

      handleSelectListKeydown(event, select) {
        const current = this.activeSelectIndex(select);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setActiveSelectOption(select, this.nextSelectIndex(select, current, event.key === 'ArrowDown' ? 1 : -1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.setActiveSelectOption(select, this.boundarySelectIndex(select, event.key === 'End' ? 1 : -1));
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.commitSelect(select, current);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.closeSelect(select, true);
        } else if (event.key === 'Tab') {
          window.setTimeout(() => this.closeSelect(select, false), 0);
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          this.selectTypeahead += event.key.toLocaleLowerCase();
          window.clearTimeout(this.selectTypeaheadTimer);
          this.selectTypeaheadTimer = window.setTimeout(() => { this.selectTypeahead = ''; }, 700);
          const options = Array.from(select.options);
          const match = options.findIndex((option) => !option.disabled && option.textContent.trim().toLocaleLowerCase().startsWith(this.selectTypeahead));
          if (match >= 0) this.setActiveSelectOption(select, match);
        }
      }

      handleSelectOutside(event) {
        if (event.target.closest('.ctm-wap_select')) return;
        this.querySelectorAll('.ctm-wap_select.is-open select').forEach((select) => this.closeSelect(select, false));
      }

      bindEvents() {
        this.backButton?.addEventListener('click', () => {
          if (this.editingFromReview) return;
          if (this.current > 0) {
            this.current -= 1;
            this.renderStep(true);
          }
        });
        this.nextButton?.addEventListener('click', () => {
          const step = this.steps[this.current];
          if (!this.validateStep(step, true)) return;
          if (this.editingFromReview) {
            this.returnToReview();
            return;
          }
          if (this.current < this.steps.length - 1) {
            this.current += 1;
            this.renderStep(true);
          }
        });
        this.form?.addEventListener('click', (event) => {
          const edit = event.target.closest('[data-review-edit]');
          if (!edit || !this.form.contains(edit)) return;
          this.editReviewStep(edit.dataset.reviewEdit);
        });
        this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
        this.startAgainButton?.addEventListener('click', () => this.restartApplication());
        this.form?.addEventListener('input', (event) => {
          if (!event.target.matches('.ctm-wap_honeypot')) this.markDirty();
          if (event.target.closest('.is-error')) this.validateControl(event.target, false);
        });
        this.form?.addEventListener('change', (event) => {
          if (!event.target.matches('.ctm-wap_honeypot')) this.markDirty();
          if (event.target.name === 'clients' || event.target.name === 'goals') this.toggleOther(event.target.name, true);
          if (event.target.name === 'otherbrands') this.toggleBrands(true);
          const group = event.target.closest('[data-group-required]');
          if (group?.classList.contains('is-error')) this.validateGroup(group, false);
        });
        this.querySelectorAll('[data-leave-link]').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (!this.shouldWarn()) return;
            if (!window.confirm(this.dataset.leaveMessage)) event.preventDefault();
            else this.dirty = false;
          });
        });
      }

      markDirty() {
        if (!this.completed) this.dirty = true;
      }

      shouldWarn() {
        return this.dirty && !this.completed && !this.designMode;
      }

      handleBeforeUnload(event) {
        if (!this.shouldWarn()) return;
        event.preventDefault();
        event.returnValue = '';
      }

      scrollCardIntoView(behavior) {
        if (!this.card) return;
        const stickyHeader = document.querySelector('.tps-header-wrapper--sticky');
        const headerBottom = stickyHeader ? Math.max(0, stickyHeader.getBoundingClientRect().bottom) : 0;
        const targetTop = window.scrollY + this.card.getBoundingClientRect().top - headerBottom;
        window.scrollTo({ top: Math.max(0, targetTop), behavior });
      }

      renderStep(moveFocus) {
        if (!this.steps.length) {
          if (this.nav) this.nav.hidden = true;
          if (this.progress) this.progress.hidden = true;
          return;
        }
        this.steps.forEach((step, index) => { step.hidden = index !== this.current; });
        const step = this.steps[this.current];
        const number = this.current + 1;
        const total = this.steps.length;
        const percent = Math.round(number / total * 100);
        this.progress.setAttribute('aria-valuemax', String(total));
        this.progress.setAttribute('aria-valuenow', String(number));
        this.progressLabel.textContent = `${this.dataset.stepPrefix} ${number} ${this.dataset.stepJoiner} ${total} · ${step.dataset.stepLabel}`;
        this.progressPercent.textContent = `${percent}%`;
        this.progressFill.style.width = `${percent}%`;
        this.backButton.hidden = this.editingFromReview || this.current === 0;
        this.nextButton.hidden = this.current === total - 1;
        this.nextButton.textContent = this.editingFromReview ? this.reviewConfirmText : this.continueButtonText;
        this.submitButton.hidden = this.current !== total - 1;
        if (step.dataset.stepType === 'review') this.buildReview();
        if (moveFocus) {
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          this.scrollCardIntoView(behavior);
          window.setTimeout(() => step.querySelector('[data-step-title]')?.focus(), behavior === 'auto' ? 0 : 250);
        }
      }

      editReviewStep(stepType) {
        const stepIndex = this.steps.findIndex((step) => step.dataset.stepType === stepType);
        const reviewIndex = this.steps.findIndex((step) => step.dataset.stepType === 'review');
        if (stepIndex < 0 || reviewIndex < 0 || stepIndex >= reviewIndex) return;
        this.editingFromReview = true;
        this.reviewEditStepType = stepType;
        this.current = stepIndex;
        this.renderStep(true);
      }

      returnToReview() {
        const stepType = this.reviewEditStepType;
        const reviewIndex = this.steps.findIndex((step) => step.dataset.stepType === 'review');
        if (reviewIndex < 0) return;
        this.editingFromReview = false;
        this.reviewEditStepType = '';
        this.current = reviewIndex;
        this.renderStep(false);
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.scrollCardIntoView(behavior);
        window.setTimeout(() => {
          const edit = this.querySelector(`[data-review-edit="${stepType}"]`);
          const heading = this.querySelector(`[data-review-heading="${stepType}"]`);
          const reviewTitle = this.steps[reviewIndex]?.querySelector('[data-step-title]');
          (edit || heading || reviewTitle)?.focus();
        }, behavior === 'auto' ? 0 : 250);
      }

      errorElement(container) {
        return container?.querySelector('[data-field-error]');
      }

      setError(control, message) {
        const container = control.closest('.ctm-wap_field');
        const error = this.errorElement(container);
        container?.classList.add('is-error');
        control.setAttribute('aria-invalid', 'true');
        if (error) {
          if (!error.id) error.id = `wap-error-${this.dataset.sectionId}-${Math.random().toString(36).slice(2, 9)}`;
          error.textContent = message;
          error.hidden = false;
          const describedBy = new Set((control.getAttribute('aria-describedby') || '').split(' ').filter(Boolean));
          describedBy.add(error.id);
          control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
        }
        if (control.matches('select')) this.syncSelectAccessibility(control);
      }

      clearError(control) {
        const container = control.closest('.ctm-wap_field');
        const error = this.errorElement(container);
        container?.classList.remove('is-error');
        control.removeAttribute('aria-invalid');
        if (error) error.hidden = true;
        if (control.matches('select')) this.syncSelectAccessibility(control);
      }

      validateControl(control, showError) {
        if (!control || control.disabled || control.closest('[data-brands-field][hidden], [data-other-field][hidden]')) return true;
        let valid = true;
        let message = this.dataset.requiredMessage;
        if (control.hasAttribute('data-required')) valid = String(control.value || '').trim() !== '';
        if (valid && control.hasAttribute('data-email') && control.value.trim()) {
          valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(control.value.trim());
          message = this.dataset.emailMessage;
        }
        if (valid && control.hasAttribute('data-phone') && control.value.trim()) {
          const phoneValue = control.value.trim();
          const digitCount = phoneValue.replace(/\D/g, '').length;
          valid = /^\+?[\d\s()-]+$/.test(phoneValue) && digitCount >= 7 && digitCount <= 15;
          message = this.dataset.phoneMessage;
        }
        if (valid && control.hasAttribute('data-zip') && control.value.trim()) {
          valid = /^\d{5}(-\d{4})?$/.test(control.value.trim());
          message = this.dataset.zipMessage;
        }
        if (!valid && showError) this.setError(control, message);
        if (valid) this.clearError(control);
        return valid;
      }

      validateGroup(group, showError) {
        const controls = Array.from(group.querySelectorAll('input[type="radio"], input[type="checkbox"]'));
        const valid = controls.some((control) => control.checked);
        const error = this.errorElement(group);
        group.classList.toggle('is-error', !valid && showError);
        controls.forEach((control) => {
          if (!valid && showError) control.setAttribute('aria-invalid', 'true');
          else control.removeAttribute('aria-invalid');
        });
        if (error) {
          error.textContent = this.dataset.requiredMessage;
          error.hidden = valid || !showError;
        }
        return valid;
      }

      validatePhotos(step, showError) {
        const photoStep = step.querySelector('[data-photo-step]');
        if (!photoStep) return true;
        const slots = ['address', 'storefront', 'interior'];
        const states = Object.fromEntries(slots.map((slot) => [slot, this.photoGroupState(slot)]));
        const ready = slots.every((slot) => states[slot].uploaded > 0);
        const pending = slots.some((slot) => states[slot].pending > 0);
        const failed = slots.some((slot) => states[slot].failed > 0);
        const consent = Boolean(this.form.elements.photoconsent?.checked);
        step.querySelectorAll('[data-photo-card]').forEach((card) => {
          const slot = card.dataset.photoCard;
          const groupInvalid = states[slot].uploaded === 0 || states[slot].failed > 0;
          card.classList.toggle('is-error', showError && groupInvalid);
          const input = card.querySelector('[data-photo-input]');
          if (showError && groupInvalid) input?.setAttribute('aria-invalid', 'true');
          else input?.removeAttribute('aria-invalid');
        });
        const consentLabel = this.form.elements.photoconsent?.closest('.ctm-wap_consent');
        consentLabel?.classList.toggle('is-error', showError && !consent);
        const error = step.querySelector('[data-step-error]');
        const valid = ready && consent && !pending && !failed;
        if (error) {
          if (pending) error.textContent = photoStep.dataset.photoPending;
          else if (failed) error.textContent = photoStep.dataset.photoFailed;
          else if (!ready && !consent) error.textContent = photoStep.dataset.bothMissing;
          else if (!ready) error.textContent = photoStep.dataset.photoMissing;
          else error.textContent = photoStep.dataset.consentMissing;
          error.hidden = valid || !showError;
        }
        if (showError && !valid) {
          const invalidSlot = slots.find((slot) => states[slot].uploaded === 0 || states[slot].pending > 0 || states[slot].failed > 0);
          if (invalidSlot) this.focusPhotoGroup(invalidSlot);
          else this.form.elements.photoconsent?.focus();
        }
        return valid;
      }

      validateStep(step, moveFocus) {
        if (!step) return true;
        let valid = true;
        let firstInvalid = null;
        step.querySelectorAll('[data-required], [data-email], [data-phone], [data-zip]').forEach((control) => {
          const controlValid = this.validateControl(control, true);
          if (!controlValid && !firstInvalid) firstInvalid = control;
          valid = controlValid && valid;
        });
        step.querySelectorAll('[data-group-required]').forEach((group) => {
          const groupValid = this.validateGroup(group, true);
          if (!groupValid && !firstInvalid) firstInvalid = group.querySelector('input');
          valid = groupValid && valid;
        });
        if (step.dataset.stepType === 'photos') {
          const photosValid = this.validatePhotos(step, false);
          valid = photosValid && valid;
          if (!photosValid && moveFocus) this.validatePhotos(step, true);
        }
        const stepError = step.querySelector('[data-step-error]');
        if (step.dataset.stepType !== 'photos' && stepError) stepError.hidden = valid;
        if (!valid && moveFocus && firstInvalid) this.focusControl(firstInvalid);
        return valid;
      }

      focusControl(control) {
        if (control.matches('select')) control.closest('.ctm-wap_select')?.querySelector('.ctm-wap_select-trigger')?.focus();
        else control.focus();
      }

      initializeConditions() {
        this.toggleOther('clients', false);
        this.toggleOther('goals', false);
        this.toggleBrands(false);
      }

      toggleOther(name, moveFocus) {
        const options = Array.from(this.form.querySelectorAll(`input[name="${name}"]`));
        const other = options.find((option) => option.hasAttribute('data-other-option'));
        const field = this.form.querySelector(`[data-other-field="${name}"]`);
        if (!field) return;
        const wasHidden = field.hidden;
        field.hidden = !other?.checked;
        if (moveFocus && wasHidden && other?.checked) field.querySelector('input')?.focus();
      }

      toggleBrands(moveFocus) {
        const selected = this.form.querySelector('input[name="otherbrands"]:checked');
        const field = this.form.querySelector('[data-brands-field]');
        const input = field?.querySelector('input');
        if (!field || !input) return;
        const show = selected?.value.trim().toLowerCase() === 'yes';
        const wasHidden = field.hidden;
        field.hidden = !show;
        input.toggleAttribute('data-required', show);
        if (!show) this.clearError(input);
        if (moveFocus && show && wasHidden) input.focus();
      }

      initializePhotos() {
        this.photoStep = this.querySelector('[data-photo-step]');
        if (!this.photoStep) return;
        this.querySelectorAll('[data-photo-input]').forEach((input) => {
          const slot = input.dataset.photoInput;
          const drop = this.querySelector(`[data-drop="${slot}"]`);
          input.addEventListener('change', () => {
            this.addPhotos(slot, Array.from(input.files || []));
            input.value = '';
          });
          ['dragenter', 'dragover'].forEach((type) => drop?.addEventListener(type, (event) => {
            event.preventDefault();
            drop.classList.add('is-dragging');
          }));
          ['dragleave', 'dragend'].forEach((type) => drop?.addEventListener(type, () => drop.classList.remove('is-dragging')));
          drop?.addEventListener('drop', (event) => {
            event.preventDefault();
            drop.classList.remove('is-dragging');
            this.addPhotos(slot, Array.from(event.dataTransfer?.files || []));
          });
        });
        this.photoStep.addEventListener('click', (event) => {
          const button = event.target.closest('[data-remove-photo]');
          if (button) {
            this.removePhoto(button.dataset.slot, button.dataset.photoId, button);
            return;
          }
          const clear = event.target.closest('[data-clear-photos]');
          if (clear) this.clearPhotos(clear.dataset.clearPhotos, clear);
        });
        this.form.elements.photoconsent?.addEventListener('change', () => {
          this.form.elements.photoconsent.closest('.ctm-wap_consent')?.classList.remove('is-error');
          this.updatePhotoCount();
        });
        this.updatePhotoCount();
      }

      fileSignature(file) {
        return `${file.name}:${file.size}:${file.lastModified}`;
      }

      fileExtensionFromName(fileName) {
        const match = String(fileName || '').toLowerCase().match(/\.([a-z0-9]+)$/);
        return match ? match[1] : '';
      }

      validPhotoType(file) {
        const type = String(file?.type || '').toLowerCase();
        return this.acceptedPhotoTypes.includes(type) || this.acceptedPhotoExtensions.includes(this.fileExtensionFromName(file?.name));
      }

      maxPhotosFor(slot) {
        const value = Number(this.querySelector(`[data-photo-input="${slot}"]`)?.dataset.maxFiles || 3);
        return Number.isFinite(value) && value > 0 ? value : 3;
      }

      photoGroupState(slot) {
        const items = this.photoItems[slot] || [];
        return {
          uploaded: items.filter((item) => item.status === 'success' && item.remoteUrl).length,
          pending: items.filter((item) => item.status === 'processing' || item.status === 'uploading').length,
          failed: items.filter((item) => item.status === 'error').length
        };
      }

      setPhotoStatus(slot, message, error) {
        const status = this.querySelector(`[data-photo-status="${slot}"]`);
        if (!status) return;
        status.textContent = message;
        status.classList.toggle('is-error', Boolean(error));
      }

      updatePhotoStatus(slot, fallbackMessage = '') {
        const state = this.photoGroupState(slot);
        if (state.pending) {
          const message = this.photoItems[slot].some((item) => item.status === 'processing') ? this.dataset.fileProcessingMessage : this.dataset.fileUploadingMessage;
          this.setPhotoStatus(slot, `${message} ${state.pending}…`, false);
        } else if (state.failed) {
          this.setPhotoStatus(slot, `${state.uploaded} ${this.dataset.fileUploadedMessage} · ${state.failed} failed`, true);
        } else if (state.uploaded) {
          this.setPhotoStatus(slot, `${state.uploaded} ${this.dataset.fileUploadedMessage}`, false);
        } else {
          this.setPhotoStatus(slot, fallbackMessage || this.photoStep.dataset.noFileText, Boolean(fallbackMessage));
        }
      }

      async addPhotos(slot, incoming) {
        if (!incoming.length) return;
        const current = this.photoItems[slot];
        const available = Math.max(0, this.maxPhotosFor(slot) - current.length);
        if (!available) {
          this.setPhotoStatus(slot, this.dataset.fileCountMessage, true);
          return;
        }
        const signatures = new Set(current.map((item) => item.signature));
        const unique = incoming.filter((file) => {
          const signature = this.fileSignature(file);
          if (signatures.has(signature)) return false;
          signatures.add(signature);
          return true;
        });
        if (!unique.length) {
          this.setPhotoStatus(slot, this.dataset.fileDuplicateMessage, true);
          return;
        }
        const accepted = unique.slice(0, available);
        const exceeded = unique.length > available;
        const items = accepted.map((file) => ({
          id: `wap-photo-${this.dataset.sectionId}-${++this.photoId}`,
          signature: this.fileSignature(file),
          file,
          fileName: file.name || 'Photo',
          previewUrl: '',
          remoteUrl: '',
          status: 'processing',
          error: '',
          removed: false
        }));
        this.photoItems[slot].push(...items);
        this.renderPhotos(slot);
        this.querySelector(`[data-photo-card="${slot}"]`)?.classList.remove('is-error');
        this.markDirty();
        this.updatePhotoCount();
        if (exceeded) this.setPhotoStatus(slot, this.dataset.fileCountMessage, true);
        await Promise.all(items.map((item) => this.prepareAndUploadPhoto(slot, item)));
      }

      async prepareAndUploadPhoto(slot, item) {
        try {
          if (!this.validPhotoType(item.file)) throw new Error(this.dataset.fileTypeMessage);
          const preparedFile = await this.prepareUploadFile(item.file);
          if (item.removed || !this.photoItems[slot].includes(item)) return;
          item.file = preparedFile;
          item.fileName = preparedFile.name || item.fileName;
          item.previewUrl = URL.createObjectURL(preparedFile);
          item.status = 'uploading';
          this.renderPhotos(slot);
          item.remoteUrl = await this.uploadFileToStorage(preparedFile);
          if (item.removed || !this.photoItems[slot].includes(item)) return;
          item.status = 'success';
          item.error = '';
        } catch (error) {
          if (item.removed || !this.photoItems[slot].includes(item)) return;
          console.error('WIN application photo upload failed.', error);
          item.remoteUrl = '';
          item.status = 'error';
          item.error = error?.message || this.dataset.fileUploadErrorMessage;
        }
        this.renderPhotos(slot);
        this.updatePhotoCount();
      }

      async prepareUploadFile(file) {
        let preparedFile = await this.normalizeUploadImageFile(file);
        if (preparedFile.size <= this.maxPhotoSize) return preparedFile;
        if (typeof window.imageCompression !== 'function') throw new Error(this.dataset.fileCompressionErrorMessage);
        const compressedBlob = await window.imageCompression(preparedFile, {
          maxSizeMB: 10,
          useWebWorker: true,
          initialQuality: 0.9,
          alwaysKeepResolution: false
        });
        if (!compressedBlob) throw new Error(this.dataset.fileCompressionErrorMessage);
        if (compressedBlob.size > this.maxPhotoSize) throw new Error(this.dataset.fileSizeMessage);
        if (compressedBlob instanceof File) return this.normalizeUploadImageFile(compressedBlob);
        const extension = this.fileExtensionForType(compressedBlob.type || preparedFile.type);
        preparedFile = new File([compressedBlob], this.renameFileWithExtension(preparedFile.name, extension), {
          type: compressedBlob.type || preparedFile.type,
          lastModified: Date.now()
        });
        return this.normalizeUploadImageFile(preparedFile);
      }

      async normalizeUploadImageFile(file) {
        if (!window.TpsUploadImageNormalizer?.normalizeUploadImageFile) throw new Error(this.dataset.fileCompressionErrorMessage);
        return window.TpsUploadImageNormalizer.normalizeUploadImageFile(file, { invalidFormatMessage: this.dataset.fileTypeMessage });
      }

      fileExtensionForType(type) {
        if (type === 'image/png') return 'png';
        if (type === 'image/webp') return 'webp';
        return 'jpg';
      }

      renameFileWithExtension(fileName, extension) {
        const baseName = String(fileName || 'upload').replace(/\.[^.]+$/, '') || 'upload';
        return `${baseName}.${extension}`;
      }

      normalizeFileNameExtension(fileName) {
        const safeName = String(fileName || 'upload').trim() || 'upload';
        const match = safeName.match(/\.([a-z0-9]+)$/i);
        if (!match) return safeName;
        const baseName = safeName.slice(0, match.index).replace(/\.+$/, '') || 'upload';
        return `${baseName}.${match[1].toLowerCase()}`;
      }

      async uploadFileToStorage(file) {
        const normalizedFile = await this.normalizeUploadImageFile(file);
        const response = await fetch(this.uploadEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: this.normalizeFileNameExtension(normalizedFile.name),
            contentType: normalizedFile.type,
            businessType: this.uploadBusinessType
          })
        });
        if (!response.ok) {
          const message = await this.readResponseError(response);
          throw new Error(message || `Upload presign failed: ${response.status}`);
        }
        const result = await response.json();
        const uploadData = result?.data || {};
        if (result?.code !== 200) throw new Error(result?.message || 'Upload presign failed.');
        if (!uploadData.uploadUrl || !uploadData.fileUrl) throw new Error('Upload presign response is missing uploadUrl or fileUrl.');
        const uploadResponse = await fetch(uploadData.uploadUrl, {
          method: uploadData.method || 'PUT',
          headers: {
            ...(uploadData.headers || {}),
            'Content-Type': uploadData.headers?.['Content-Type'] || normalizedFile.type
          },
          body: normalizedFile
        });
        if (!uploadResponse.ok) {
          const message = await this.readResponseError(uploadResponse);
          throw new Error(message || `Storage upload failed: ${uploadResponse.status}`);
        }
        return uploadData.fileUrl;
      }

      async readResponseError(response) {
        try {
          const message = await response.text();
          return message ? `${response.status} ${message}` : '';
        } catch (error) {
          return '';
        }
      }

      renderPhotos(slot) {
        const container = this.querySelector(`[data-photo-previews="${slot}"]`);
        if (!container) return;
        container.replaceChildren();
        this.photoItems[slot].forEach((item) => {
          const preview = document.createElement('div');
          preview.className = 'ctm-wap_preview';
          preview.setAttribute('role', 'listitem');
          const media = document.createElement('div');
          media.className = 'ctm-wap_preview-media';
          if (item.previewUrl) {
            const image = document.createElement('img');
            image.src = item.previewUrl;
            image.alt = `${item.fileName} preview`;
            media.append(image);
          } else {
            const placeholder = document.createElement('span');
            placeholder.textContent = item.fileName;
            media.append(placeholder);
          }
          const bar = document.createElement('div');
          bar.className = 'ctm-wap_preview-bar';
          const name = document.createElement('span');
          name.className = 'ctm-wap_preview-name';
          name.textContent = item.fileName;
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'ctm-wap_remove';
          remove.dataset.removePhoto = '';
          remove.dataset.slot = slot;
          remove.dataset.photoId = item.id;
          remove.setAttribute('aria-label', `${this.photoStep.dataset.removeLabel} ${item.fileName}`);
          remove.textContent = '×';
          bar.append(name, remove);
          preview.append(media, bar);
          if (item.status !== 'success') {
            const state = document.createElement('span');
            state.className = `ctm-wap_preview-state${item.status === 'error' ? ' is-error' : ''}`;
            if (item.status === 'processing') state.textContent = `${this.dataset.fileProcessingMessage}…`;
            else if (item.status === 'uploading') state.textContent = `${this.dataset.fileUploadingMessage}…`;
            else state.textContent = item.error || this.dataset.fileUploadErrorMessage;
            preview.append(state);
          }
          container.append(preview);
        });
        this.syncPhotoInputState(slot);
        this.updatePhotoStatus(slot);
        const clear = this.querySelector(`[data-clear-photos="${slot}"]`);
        if (clear) clear.hidden = this.photoItems[slot].length === 0;
        const state = this.photoGroupState(slot);
        if (state.uploaded > 0 && state.failed === 0) {
          this.querySelector(`[data-photo-card="${slot}"]`)?.classList.remove('is-error');
          this.querySelector(`[data-photo-input="${slot}"]`)?.removeAttribute('aria-invalid');
        }
      }

      removePhoto(slot, photoId, button) {
        const items = this.photoItems[slot];
        const index = items.findIndex((item) => item.id === photoId);
        const item = items[index];
        if (!item) return;
        item.removed = true;
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        items.splice(index, 1);
        this.renderPhotos(slot);
        this.markDirty();
        this.updatePhotoCount();
        this.setPhotoStatus(slot, `${item.fileName} ${this.dataset.fileRemovedMessage}`, false);
        const buttons = Array.from(this.querySelectorAll(`[data-remove-photo][data-slot="${slot}"]`));
        const focusTarget = buttons[Math.min(index, buttons.length - 1)] || this.querySelector(`[data-photo-input="${slot}"]`);
        focusTarget?.focus();
      }

      clearPhotos(slot, button) {
        const items = this.photoItems[slot] || [];
        if (!items.length) return;
        items.forEach((item) => {
          item.removed = true;
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        this.photoItems[slot] = [];
        this.renderPhotos(slot);
        this.markDirty();
        this.updatePhotoCount();
        this.setPhotoStatus(slot, `${this.photoStep.dataset.removeLabel} ${this.dataset.fileRemovedMessage}`, false);
        this.querySelector(`[data-photo-input="${slot}"]`)?.focus();
      }

      syncPhotoInputState(slot) {
        const input = this.querySelector(`[data-photo-input="${slot}"]`);
        const drop = this.querySelector(`[data-drop="${slot}"]`);
        if (!input || !drop) return;
        const hasPhotos = this.photoItems[slot].length > 0;
        const full = this.photoItems[slot].length >= this.maxPhotosFor(slot);
        input.disabled = full;
        drop.classList.toggle('has-photos', hasPhotos);
        drop.classList.toggle('is-full', full);
        drop.setAttribute('aria-disabled', full ? 'true' : 'false');
      }

      focusPhotoGroup(slot) {
        const input = this.querySelector(`[data-photo-input="${slot}"]`);
        if (input && !input.disabled) input.focus();
        else this.querySelector(`[data-remove-photo][data-slot="${slot}"]`)?.focus();
      }

      updatePhotoCount() {
        const slots = ['address', 'storefront', 'interior'];
        const count = slots.filter((slot) => this.photoGroupState(slot).uploaded > 0).length;
        const output = this.querySelector('[data-photo-count]');
        if (output) output.textContent = String(count);
        const settled = slots.every((slot) => {
          const state = this.photoGroupState(slot);
          return state.uploaded > 0 && state.pending === 0 && state.failed === 0;
        });
        if (settled && this.form.elements.photoconsent?.checked) {
          const error = this.photoStep?.closest('[data-step]')?.querySelector('[data-step-error]');
          if (error) error.hidden = true;
        }
      }

      controlValue(name) {
        const controls = Array.from(this.form.querySelectorAll(`[name="${name}"]`));
        if (!controls.length) return '';
        if (controls[0].type === 'radio') return controls.find((control) => control.checked)?.value || '';
        if (controls[0].type === 'checkbox') return controls.filter((control) => control.checked).map((control) => control.value);
        return controls[0].value.trim();
      }

      optionValues(name, otherName) {
        return this.controlValue(name).map((value) => value === 'Other' ? (this.controlValue(otherName) || 'Other') : value);
      }

      fieldLabel(name, fallback) {
        const control = this.form.querySelector(`[name="${name}"]`);
        if (!control) return fallback;
        const fieldset = control.closest('fieldset');
        const label = fieldset?.querySelector('legend') || this.form.querySelector(`label[for="${control.id}"], label[for="${control.id}-trigger"]`);
        return label?.firstChild?.textContent?.trim() || fallback;
      }

      appendReviewGroup(container, stepType, heading, rows) {
        const header = document.createElement('div');
        const title = document.createElement('h3');
        const edit = document.createElement('button');
        const editText = this.dataset.reviewEditText?.trim() || 'Edit';
        header.className = 'ctm-wap_review-heading';
        header.dataset.reviewHeading = stepType;
        title.className = 'ctm-wap_review-heading-title';
        title.textContent = heading;
        title.tabIndex = -1;
        edit.className = 'ctm-wap_review-edit';
        edit.type = 'button';
        edit.dataset.reviewEdit = stepType;
        edit.textContent = editText;
        edit.setAttribute('aria-label', `${editText} ${heading}`);
        header.append(title, edit);
        container.append(header);
        rows.forEach(([label, value]) => {
          const row = document.createElement('div');
          row.className = 'ctm-wap_review-row';
          const term = document.createElement('span');
          const description = document.createElement('strong');
          term.textContent = label;
          description.textContent = Array.isArray(value) ? (value.join(', ') || '—') : (value || '—');
          row.append(term, description);
          container.append(row);
        });
      }

      appendReviewPhotos(container) {
        const slots = ['address', 'storefront', 'interior'];
        const wrapper = document.createElement('div');
        wrapper.className = 'ctm-wap_review-photos';
        slots.forEach((slot) => {
          const photos = this.photoItems[slot].filter((item) => item.status === 'success' && item.remoteUrl);
          if (!photos.length) return;
          const group = document.createElement('div');
          const title = document.createElement('h4');
          const list = document.createElement('ul');
          group.className = 'ctm-wap_review-photo-group';
          title.className = 'ctm-wap_review-photo-title';
          title.textContent = this.querySelector(`[data-photo-card="${slot}"] h3`)?.textContent?.trim() || slot;
          list.className = 'ctm-wap_review-photo-list';
          photos.forEach((photo) => {
            const item = document.createElement('li');
            const image = document.createElement('img');
            image.src = photo.remoteUrl;
            image.alt = `${photo.fileName} uploaded preview`;
            image.loading = 'lazy';
            item.append(image);
            list.append(item);
          });
          group.append(title, list);
          wrapper.append(group);
        });
        if (wrapper.childElementCount) container.append(wrapper);
      }

      stepHeading(type, fallback) {
        return this.steps.find((step) => step.dataset.stepType === type)?.dataset.reviewHeading || fallback;
      }

      buildReview() {
        const container = this.querySelector('[data-review]');
        if (!container) return;
        container.replaceChildren();
        const contact = [this.controlValue('contact'), this.controlValue('role')].filter(Boolean).join(' · ');
        const address = [this.controlValue('saddr'), [this.controlValue('scity'), this.controlValue('sstate'), this.controlValue('szip')].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
        const brands = this.controlValue('otherbrands');
        const brandsValue = brands.toLowerCase() === 'yes' && this.controlValue('otherwhich') ? `${brands} — ${this.controlValue('otherwhich')}` : brands;
        this.appendReviewGroup(container, 'studio', this.stepHeading('studio', 'Your studio'), [
          [this.fieldLabel('studio', 'Studio'), this.controlValue('studio')],
          [this.fieldLabel('contact', 'Contact'), contact],
          [this.fieldLabel('email', 'Email'), this.controlValue('email')],
          [this.fieldLabel('phone', 'Phone'), this.controlValue('phone')],
          [this.fieldLabel('web', 'Website'), this.controlValue('web')],
          [this.fieldLabel('instagram', 'Instagram or Facebook'), this.controlValue('instagram')]
        ]);
        this.appendReviewGroup(container, 'space', this.stepHeading('space', 'Your space'), [
          [this.fieldLabel('businessType', 'Business type'), this.controlValue('businessType')],
          [this.fieldLabel('saddr', 'Address'), address],
          [this.fieldLabel('hours', 'Visits'), this.controlValue('hours')],
          [this.fieldLabel('sqft', 'Display space'), this.controlValue('sqft')],
          [this.fieldLabel('own', 'Tenure'), this.controlValue('own')]
        ]);
        this.appendReviewGroup(container, 'business', this.stepHeading('business', 'Business at a glance'), [
          [this.fieldLabel('founded', 'Time in business'), this.controlValue('founded')],
          [this.fieldLabel('team', 'Team size'), this.controlValue('team')],
          [this.fieldLabel('revenue', 'Annual revenue'), this.controlValue('revenue')],
          [this.fieldLabel('share', 'Window share'), this.controlValue('share')],
          [this.fieldLabel('volume', 'Projects / month'), this.controlValue('volume')],
          [this.fieldLabel('avgorder', 'Typical project'), this.controlValue('avgorder')],
          [this.fieldLabel('clients', 'Customers'), this.optionValues('clients', 'clientsOther')]
        ]);
        this.appendReviewGroup(container, 'service', this.stepHeading('service', 'Service & goals'), [
          [this.fieldLabel('radius', 'Service area'), this.controlValue('radius')],
          [this.fieldLabel('install', 'Measure & install'), this.controlValue('install')],
          [this.fieldLabel('otherbrands', 'Other brands'), brandsValue],
          [this.fieldLabel('goals', 'Looking for'), this.optionValues('goals', 'goalsOther')]
        ]);
        this.appendReviewGroup(container, 'photos', this.stepHeading('photos', 'Studio verification photos'), [
          ['Required photo groups', `${Object.values(this.photoItems).filter((items) => items.some((item) => item.status === 'success' && item.remoteUrl)).length}/3 ready`],
          ['Interior views', `${this.photoItems.interior.filter((item) => item.status === 'success' && item.remoteUrl).length} uploaded`],
          [this.fieldLabel('photoNote', 'Photo note'), this.controlValue('photoNote') || 'None'],
          ['Storage permission', this.form.elements.photoconsent?.checked ? 'Confirmed' : 'Not confirmed']
        ]);
        this.appendReviewPhotos(container);
      }

      buildPayload() {
        const uploadedPhotoUrls = (slot) => this.photoItems[slot]
          .filter((item) => item.status === 'success' && item.remoteUrl)
          .map((item) => item.remoteUrl);
        const hasOtherBrands = this.controlValue('otherbrands').toLowerCase() === 'yes';
        return {
          shopId: this.dataset.shopId || null,
          studioName: this.controlValue('studio'),
          contactName: this.controlValue('contact'),
          role: this.controlValue('role'),
          email: this.controlValue('email'),
          phone: this.controlValue('phone'),
          web: this.controlValue('web'),
          instagram: this.controlValue('instagram'),
          businessType: this.controlValue('businessType'),
          address: this.controlValue('saddr'),
          city: this.controlValue('scity'),
          state: this.controlValue('sstate'),
          zipcode: this.controlValue('szip'),
          visits: this.controlValue('hours'),
          displaySpace: this.controlValue('sqft'),
          tenure: this.controlValue('own'),
          founded: this.controlValue('founded'),
          team: this.controlValue('team'),
          revenue: this.controlValue('revenue'),
          share: this.controlValue('share'),
          volume: this.controlValue('volume'),
          projectValue: this.controlValue('avgorder'),
          clients: this.controlValue('clients'),
          clientsOther: this.controlValue('clients').includes('Other') ? this.controlValue('clientsOther') : '',
          radius: this.controlValue('radius'),
          install: this.controlValue('install'),
          brands: hasOtherBrands,
          brandsWhich: hasOtherBrands ? this.controlValue('otherwhich') : '',
          goals: this.controlValue('goals'),
          goalsOther: this.controlValue('goals').includes('Other') ? this.controlValue('goalsOther') : '',
          note: this.controlValue('photoNote'),
          photos: [
            { groupCode: 'ADDRESS', photoUrls: uploadedPhotoUrls('address') },
            { groupCode: 'STOREFRONT', photoUrls: uploadedPhotoUrls('storefront') },
            { groupCode: 'INTERIOR', photoUrls: uploadedPhotoUrls('interior') }
          ]
        };
      }

      setSubmitBusy(busy) {
        this.submitting = busy;
        if (!this.submitButton) return;
        this.submitButton.disabled = busy;
        this.submitButton.textContent = busy ? (this.dataset.submittingText?.trim() || 'Sending application…') : this.submitButtonText;
        this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      }

      async submitApplication(payload) {
        const response = await fetch(this.submitEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const responseText = await response.text();
        let result = null;
        if (responseText) {
          try {
            result = JSON.parse(responseText);
          } catch (error) {
            if (response.ok) return responseText;
          }
        }
        if (!response.ok) throw new Error(result?.message || `Application submission failed: ${response.status}`);
        if (result?.code !== undefined && Number(result.code) !== 200) throw new Error(result.message || 'Application submission failed.');
        if (result?.success === false) throw new Error(result.message || 'Application submission failed.');
        return result;
      }

      resetValidationState() {
        const errors = Array.from(this.form.querySelectorAll('[data-field-error]'));
        const errorIds = new Set(errors.map((error) => error.id).filter(Boolean));
        this.form.querySelectorAll('[aria-describedby]').forEach((control) => {
          const describedBy = (control.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && !errorIds.has(id));
          if (describedBy.length) control.setAttribute('aria-describedby', describedBy.join(' '));
          else control.removeAttribute('aria-describedby');
        });
        this.form.querySelectorAll('[aria-invalid]').forEach((control) => control.removeAttribute('aria-invalid'));
        this.form.querySelectorAll('.is-error').forEach((container) => container.classList.remove('is-error'));
        errors.forEach((error) => {
          error.textContent = '';
          error.hidden = true;
        });
        this.form.querySelectorAll('[data-step-error]').forEach((error) => { error.hidden = true; });
        if (this.submitError) {
          this.submitError.textContent = '';
          this.submitError.hidden = true;
        }
      }

      resetPhotoState() {
        const slots = ['address', 'storefront', 'interior'];
        Object.values(this.photoItems).flat().forEach((item) => {
          item.removed = true;
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        this.photoItems = { address: [], storefront: [], interior: [] };
        this.photoId = 0;
        slots.forEach((slot) => {
          const input = this.querySelector(`[data-photo-input="${slot}"]`);
          if (input) input.value = '';
          this.querySelector(`[data-photo-card="${slot}"]`)?.classList.remove('is-error');
          this.renderPhotos(slot);
        });
        this.updatePhotoCount();
      }

      restartApplication() {
        this.completed = false;
        this.dirty = false;
        this.setSubmitBusy(false);
        this.editingFromReview = false;
        this.reviewEditStepType = '';
        this.current = 0;
        this.form.reset();
        this.resetValidationState();
        this.querySelectorAll('.ctm-wap_select select').forEach((select) => {
          this.closeSelect(select, false);
          this.syncSelect(select);
        });
        this.initializeConditions();
        this.resetPhotoState();
        this.querySelector('[data-review]')?.replaceChildren();
        this.success.hidden = true;
        this.intro.hidden = false;
        this.form.hidden = false;
        this.progress.hidden = false;
        this.classList.remove('is-complete');
        this.renderStep(false);
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.scrollCardIntoView(behavior);
        window.setTimeout(() => this.steps[0]?.querySelector('[data-step-title]')?.focus(), behavior === 'auto' ? 0 : 250);
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (this.submitting || this.form.elements.company_website?.value) return;
        let firstInvalidStep = -1;
        this.steps.forEach((step, index) => {
          if (step.dataset.stepType === 'review') return;
          if (!this.validateStep(step, false) && firstInvalidStep < 0) firstInvalidStep = index;
        });
        if (firstInvalidStep >= 0) {
          this.current = firstInvalidStep;
          this.renderStep(false);
          this.validateStep(this.steps[this.current], true);
          return;
        }
        const payload = this.buildPayload();
        if (this.submitError) {
          this.submitError.textContent = '';
          this.submitError.hidden = true;
        }
        this.setSubmitBusy(true);
        try {
          const response = await this.submitApplication(payload);
          this.dispatchEvent(new CustomEvent('ctm:winapply:submit', {
            bubbles: true,
            detail: {
              data: payload,
              response,
              files: {
                address: this.photoItems.address.filter((item) => item.status === 'success').map((item) => item.file),
                storefront: this.photoItems.storefront.filter((item) => item.status === 'success').map((item) => item.file),
                interior: this.photoItems.interior.filter((item) => item.status === 'success').map((item) => item.file)
              }
            }
          }));
          this.completed = true;
          this.dirty = false;
          this.classList.add('is-complete');
          this.form.hidden = true;
          this.progress.hidden = true;
          this.intro.hidden = true;
          this.success.hidden = false;
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          this.scrollCardIntoView(behavior);
          this.success.querySelector('[data-success-title]')?.focus({ preventScroll: true });
        } catch (error) {
          console.error('WIN application submission failed.', error);
          if (this.submitError) {
            this.submitError.textContent = this.dataset.submitErrorMessage?.trim() || 'We could not submit your application. Please check your connection and try again.';
            this.submitError.hidden = false;
            this.submitError.focus?.();
          }
        } finally {
          if (!this.completed) this.setSubmitBusy(false);
        }
      }
    });
  }
